﻿import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getJourneyData } from "../api/journeyService";
import {
  generateOtp,
  resolvePreferredOtpIdentifier,
  verifyOtp,
} from "../api/otpService";
import {
  DEFAULT_ROUTE_MAP,
  RESUME_DOB_SESSION_KEY,
  getLegacyResumeJourneyUrl,
  getResumeDobStorageKey,
} from "../constants";
import {
  decryptJourneyQueryValue,
  encryptJourneyQueryValue,
} from "../common/utils/journeyEncryption";
import { logError } from "../common/utils/logger";
import "./ResumeJourney.scss";

const APPLICATION_NUMBER_REGEX = /^N\d{7}$/;
const DOB_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const OTP_REGEX = /^\d{6}$/;
const OTP_RESEND_COOLDOWN_SECONDS = 30;

const ymdStringToLocalDate = (ymd) => {
  if (!ymd || typeof ymd !== "string" || !DOB_REGEX.test(ymd.trim()))
    return null;
  const [y, m, d] = ymd
    .trim()
    .split("-")
    .map((p) => Number(p));
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
    return null;
  }
  return dt;
};

const localDateToYmd = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
};

const formatDdMmYyyyDigits = (digits) => {
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const eightDigitsToIso = (digits8) => {
  if (!digits8 || digits8.length !== 8) return "";
  const dd = digits8.slice(0, 2);
  const mm = digits8.slice(2, 4);
  const yyyy = digits8.slice(4, 8);
  const d = Number(dd);
  const mo = Number(mm);
  const y = Number(yyyy);
  if (!Number.isFinite(d) || !Number.isFinite(mo) || !Number.isFinite(y))
    return "";
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return "";
  const dt = new Date(y, mo - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== mo - 1 ||
    dt.getDate() !== d
  ) {
    return "";
  }
  return `${yyyy}-${mm}-${dd}`;
};

const handleDobRawInputChange = (e, onDobChange) => {
  if (e.nativeEvent instanceof MouseEvent) return;

  const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
  e.target.value = formatDdMmYyyyDigits(digits);

  if (digits.length === 8) {
    const iso = eightDigitsToIso(digits);
    if (iso) onDobChange(iso);
  } else if (digits.length === 0) {
    onDobChange("");
  }
};

const minResumeDobDate = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 120);
  return d;
})();

const parseJsonSafe = (value) => {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const maskMobile = (mobile) => {
  const digits = String(mobile || "").replace(/\D/g, "");
  if (digits.length < 4) return "your registered mobile number";
  return `******${digits.slice(-4)}`;
};

const sanitizeRoutePath = (pathValue) => {
  const raw = String(pathValue || "").trim();
  if (!raw) return "";
  return raw.startsWith("/") ? raw : `/${raw}`;
};

const parseRouteMap = (routeMapAttr) => {
  const map = { ...DEFAULT_ROUTE_MAP };
  const raw = String(routeMapAttr || "").trim();
  if (!raw) return map;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      Object.entries(parsed).forEach(([key, value]) => {
        const normalizedKey = String(key || "")
          .trim()
          .toLowerCase();
        const normalizedPath = sanitizeRoutePath(value);
        if (normalizedKey && normalizedPath)
          map[normalizedKey] = normalizedPath;
      });
      return map;
    }
  } catch {
    // fall back to key:value list parser
  }

  raw.split(",").forEach((pair) => {
    const [key, value] = pair.split(":");
    const normalizedKey = String(key || "")
      .trim()
      .toLowerCase();
    const normalizedPath = sanitizeRoutePath(value);
    if (normalizedKey && normalizedPath) map[normalizedKey] = normalizedPath;
  });

  return map;
};

const extractLeadStages = (journey) => {
  const stages = parseJsonSafe(
    journey?.stages ??
      journey?.cummulativeJourneyData ??
      journey?.c_cummulativeJourneyData,
  );
  if (!stages || typeof stages !== "object") return {};
  return stages;
};

const preferredLeadStageOrder = [
  "Stage 1.0 Lead",
  "Stage 1.1 Lead",
  "Stage 1.2 Lead",
  "Stage 2.0 Lead",
  "lead_basic",
  "lead_personal",
  "lead_primary_annuitant",
  "lead_residency",
  "lead_additional",
];

const readNameFromStageData = (stageData) => {
  if (!stageData || typeof stageData !== "object") return "";
  const direct =
    stageData.fullName ||
    stageData.name ||
    stageData.customerName ||
    stageData.lifeAssuredName ||
    stageData.proposerName ||
    stageData.lifeAssuredFullName ||
    "";
  if (String(direct).trim()) return String(direct).trim();

  const leadInput = stageData.leadInput;
  if (leadInput && typeof leadInput === "object") {
    const fromLeadInput =
      leadInput.lifeAssuredName ||
      leadInput.fullName ||
      leadInput.name ||
      leadInput.customerName ||
      "";
    if (String(fromLeadInput).trim()) return String(fromLeadInput).trim();
  }

  const parts = [stageData.firstName, stageData.middleName, stageData.lastName]
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  return parts.join(" ");
};

const readMobileFromStageData = (stageData) => {
  if (!stageData || typeof stageData !== "object") return "";
  const candidates = [
    stageData.mobileNumber,
    stageData.proposerMobileNumber,
    stageData.mobile,
    stageData.contactNo,
    stageData.phone,
    stageData.leadInput?.mobileNumber,
    stageData.leadInput?.proposerMobileNumber,
    stageData.paymentData?.mobileNumber,
    stageData.paymentData?.proposerMobileNumber,
    stageData.pennyDropRequest?.mobile,
  ];
  for (const candidate of candidates) {
    const mobile = String(candidate ?? "").trim();
    if (mobile) return mobile;
  }
  return "";
};

const extractMobileFromJourney = (journey) => {
  const stages = extractLeadStages(journey);

  for (const stageName of preferredLeadStageOrder) {
    const mobile = readMobileFromStageData(stages?.[stageName]);
    if (mobile) return mobile;
  }
  for (const [stageName, stageData] of Object.entries(stages)) {
    if (!/lead/i.test(String(stageName))) continue;
    const mobile = readMobileFromStageData(stageData);
    if (mobile) return mobile;
  }
  for (const stageData of Object.values(stages)) {
    const mobile = readMobileFromStageData(stageData);
    if (mobile) return mobile;
  }
  return "";
};

/** Customer name for OTP SMS/email placeholders ({Customer Name}). */
const extractCustomerNameFromJourney = (journey) => {
  const stages = extractLeadStages(journey);

  // Prefer later lead stages — Stage 1.0 often has mobile/DOB only; name lands on 1.1/2.0.
  for (const stageName of [...preferredLeadStageOrder].reverse()) {
    const name = readNameFromStageData(stages?.[stageName]);
    if (name) return name;
  }
  for (const [stageName, stageData] of Object.entries(stages)) {
    if (!/lead/i.test(String(stageName))) continue;
    const name = readNameFromStageData(stageData);
    if (name) return name;
  }
  // Proposal / quote stages as last resort (resume after deeper progress).
  for (const stageData of Object.values(stages)) {
    const name = readNameFromStageData(stageData);
    if (name) return name;
  }
  return "";
};

const extractJourneySignals = (journey) => {
  const stages = extractLeadStages(journey);
  const primaryLeadStage =
    stages["Stage 1.0 Lead"] ||
    stages["Stage 1.1 Lead"] ||
    stages["Stage 1.2 Lead"] ||
    stages["Stage 2.0 Lead"] ||
    {};

  const stageSignals = Object.values(stages)
    .filter((stageData) => stageData && typeof stageData === "object")
    .flatMap((stageData) => [
      stageData?.productLob,
      stageData?.journeyType,
      stageData?.planName,
      stageData?.productName,
      stageData?.selectedPlan,
    ]);

  return [
    journey?.productLob,
    journey?.journeyType,
    journey?.planName,
    journey?.productName,
    journey?.selectedPlan,
    primaryLeadStage?.productLob,
    primaryLeadStage?.journeyType,
    primaryLeadStage?.planName,
    primaryLeadStage?.productName,
    primaryLeadStage?.selectedPlan,
    ...stageSignals,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());
};

const resolveJourneyPath = (journey, routeMap) => {
  const signals = extractJourneySignals(journey);
  const normalizedMap = parseRouteMap(routeMap);

  const keyBySignalPriority = Object.keys(normalizedMap).find((key) =>
    signals.some((signal) => signal.includes(key)),
  );
  if (keyBySignalPriority) return normalizedMap[keyBySignalPriority];

  // Backward-compatible fallback for current products.
  if (signals.some((s) => s.includes("nischit") || s.includes("pension"))) {
    return normalizedMap.nischit || DEFAULT_ROUTE_MAP.nischit;
  }
  if (
    signals.some(
      (s) =>
        s.includes("tulip") ||
        s.includes("prosper") ||
        s.includes("80012"),
    )
  ) {
    return normalizedMap.tulip || DEFAULT_ROUTE_MAP.tulip;
  }
  if (
    signals.some(
      (s) =>
        s.includes("health") ||
        s.includes("cancer") ||
        s.includes("22060") ||
        s.includes("22073"),
    )
  ) {
    return normalizedMap.health || DEFAULT_ROUTE_MAP.health;
  }
  return normalizedMap.term || DEFAULT_ROUTE_MAP.term;
};

const readApplicationNumberFromUrl = () => {
  try {
    if (typeof window === "undefined" || !window.location?.search) return "";
    const params = new URLSearchParams(window.location.search);
    const raw =
      params.get("applicationNumber")?.trim() ||
      params.get("applicationNo")?.trim() ||
      "";
    if (!raw) return "";
    const plain = (decryptJourneyQueryValue(raw) || raw).trim().toUpperCase();
    return APPLICATION_NUMBER_REGEX.test(plain) ? plain : "";
  } catch {
    return "";
  }
};

function ResumeJourney({ routeMap }) {
  const [applicationNumber, setApplicationNumber] = useState(
    () => readApplicationNumberFromUrl(),
  );
  const [dob, setDob] = useState("");
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [applicationError, setApplicationError] = useState("");
  const [dobError, setDobError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpIdentifier, setOtpIdentifier] = useState("");
  const [maskedOtpDestination, setMaskedOtpDestination] = useState("");
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [validatedJourney, setValidatedJourney] = useState(null);

  const normalizedApplicationNumber = applicationNumber.trim().toUpperCase();
  const isApplicationNumberValid = APPLICATION_NUMBER_REGEX.test(
    normalizedApplicationNumber,
  );
  const isDobValid = DOB_REGEX.test(dob);
  const isOtpValid = OTP_REGEX.test(otp.trim());

  const isSendOtpDisabled = useMemo(
    () => isLoading || !isApplicationNumberValid || !isDobValid,
    [isApplicationNumberValid, isDobValid, isLoading],
  );
  const isVerifyOtpDisabled = useMemo(
    () => isLoading || !isOtpValid,
    [isLoading, isOtpValid],
  );
  const isResendOtpDisabled = isLoading || resendCooldownSeconds > 0;

  let resendOtpLabel = "Resend OTP";
  if (isLoading && otpSent) {
    resendOtpLabel = "Please wait...";
  } else if (resendCooldownSeconds > 0) {
    resendOtpLabel = `Resend OTP in 00:${String(resendCooldownSeconds).padStart(
      2,
      "0",
    )}`;
  }

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldownSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldownSeconds]);

  const resetOtpState = () => {
    setOtpSent(false);
    setOtp("");
    setOtpIdentifier("");
    setMaskedOtpDestination("");
    setOtpError("");
    setErrorMessage("");
    setResendCooldownSeconds(0);
    setValidatedJourney(null);
  };

  const storeResumeDob = (applicationNo, dobValue) => {
    try {
      sessionStorage.setItem(getResumeDobStorageKey(applicationNo), dobValue);
      sessionStorage.setItem(RESUME_DOB_SESSION_KEY, dobValue);
    } catch {
      // ignore session storage failures
    }
  };

  const onSendOtp = async () => {
    const appNo = normalizedApplicationNumber;
    if (!appNo) {
      setApplicationError("Please enter application number.");
      return;
    }
    if (!isApplicationNumberValid) {
      setApplicationError(
        "Application number must be in format N followed by 7 digits (example: N0000061).",
      );
      return;
    }
    if (!dob) {
      setDobError("Please select date of birth.");
      return;
    }
    if (!isDobValid) {
      setDobError("DOB year must be exactly 4 digits (example: 1997).");
      return;
    }

    setApplicationError("");
    setDobError("");
    setErrorMessage("");
    setOtpError("");
    setIsLoading(true);

    try {
      const journey = await getJourneyData(appNo, dob);
      const mobile = extractMobileFromJourney(journey);
      if (!mobile) {
        throw new Error(
          "Unable to find a registered mobile number for this application.",
        );
      }
      const identifier = resolvePreferredOtpIdentifier({ mobile });
      if (!identifier) {
        throw new Error("Unable to prepare OTP recipient. Please try again.");
      }
      const customerName = extractCustomerNameFromJourney(journey);
      await generateOtp({
        mobile,
        applicationNumber: appNo,
        customerName: customerName || undefined,
      });
      setOtpIdentifier(identifier);
      setMaskedOtpDestination(maskMobile(mobile));
      setOtpSent(true);
      setOtp("");
      setValidatedJourney(journey);
      setResendCooldownSeconds(OTP_RESEND_COOLDOWN_SECONDS);
      storeResumeDob(appNo, dob);
    } catch (error) {
      logError("[DropoffRecoveryJourney] failed to send OTP", error);
      const apiMessage =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      setErrorMessage(
        apiMessage ||
          "Unable to resume journey right now. Please check details and try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onVerifyAndResume = async () => {
    const appNo = normalizedApplicationNumber;
    const otpValue = otp.trim();

    if (!otpIdentifier) {
      setOtpError("Please request OTP again.");
      return;
    }
    if (!OTP_REGEX.test(otpValue)) {
      setOtpError("Please enter a valid 6-digit OTP.");
      return;
    }

    setErrorMessage("");
    setOtpError("");
    setIsLoading(true);

    try {
      await verifyOtp(otpIdentifier, otpValue);
      const journey = validatedJourney || (await getJourneyData(appNo, dob));
      const journeyPath = resolveJourneyPath(journey, routeMap);
      const encryptedAppNum = encryptJourneyQueryValue(appNo) || appNo;
      window.location.href = `${journeyPath}?applicationNumber=${encodeURIComponent(
        encryptedAppNum,
      )}`;
    } catch (error) {
      logError("[DropoffRecoveryJourney] OTP verification failed", error);
      const apiMessage =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      setOtpError(
        apiMessage ||
          "OTP verification failed. Please re-enter OTP and try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onResendOtp = async () => {
    if (isResendOtpDisabled) return;
    await onSendOtp();
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (otpSent) {
      if (!isVerifyOtpDisabled) onVerifyAndResume();
      return;
    }
    if (!isSendOtpDisabled) onSendOtp();
  };

  const handleStartNewJourney = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/home";
  };

  const handleResumeLegacyJourney = () => {
    window.open(getLegacyResumeJourneyUrl(), "_blank", "noopener,noreferrer");
  };

  const buttonLabel = getButtonLabel();

  function getButtonLabel() {
    if (isLoading) {
      return "Please wait...";
    }

    if (otpSent) {
      return "Verify OTP & Resume Journey";
    }

    return "Send OTP";
  } 
  return (
    <div className="resume-page">
      <div className="resume-page-content">
        <div className="resume-card-left">
          <div className="resume-card-image">
            <svg
              width="169"
              height="150"
              viewBox="0 0 169 150"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M164.105 149.295H111.867L115.994 90.1999C116.24 86.6746 119.298 83.8168 122.823 83.8168H162.295C165.821 83.8168 168.479 86.6746 168.233 90.1999L164.105 149.295Z"
                fill="#1282D6"
              />
              <path
                d="M156.422 149.295H104.184L108.31 90.1999C108.556 86.6746 111.614 83.8168 115.139 83.8168H154.612C158.137 83.8168 160.795 86.6746 160.549 90.1999L156.422 149.295Z"
                fill="#3897E9"
              />
              <path
                d="M135.251 83.8173C137.2 85.6335 137.968 87.3549 138.29 88.6675C138.947 91.3485 138.386 94.9374 138.384 94.9702C139.392 96.1665 140.677 97.7783 142.055 99.7625C142.055 99.7625 145.138 104.2 147.312 108.805C150.022 114.542 154.413 123.843 152.364 133.737C150.794 141.319 145.433 149.295 145.433 149.295H115.711L119.353 83.8096L135.251 83.8173Z"
                fill="#1282D6"
              />
              <path
                d="M79.6394 46.7477C79.7854 46.2795 82.1765 38.9296 87.7926 36.4268C90.4803 35.2295 93.5097 35.1148 93.5097 35.1148C93.8228 35.103 95.1159 35.064 96.8578 35.3499C96.8578 35.3499 100.878 35.8366 103.809 37.9146C108.966 41.5722 111.955 50.7296 111.405 58.6937C111.21 61.5254 110.538 64.4339 112.379 66.6097C113.373 67.7835 115.02 68.1734 117.358 68.8896C122.214 70.3774 130.202 72.2572 133.413 78.7576C136.624 85.258 137.015 94.4211 134.9 96.9274C132.786 99.4337 82.3205 99.1986 80.5334 97.2404C78.7464 95.2823 75.4573 85.8062 75.0653 78.2873C74.717 71.6039 79.3991 68.8358 80.5334 60.5975C81.3235 54.8605 80.4745 49.9713 79.6394 46.7477Z"
                fill="#262626"
              />
              <path
                d="M108.43 46.0212C109.397 47.9087 110.785 50.9524 111.797 54.8912C113.396 61.1145 112.133 62.1074 113.599 64.5041C116.852 69.8267 123.915 66.5764 130.672 72.6491C134.462 76.0551 135.774 80.2541 136.216 81.7342C138.138 88.1594 136.346 93.6705 135.588 95.6604"
                stroke="#262626"
                stroke-width="0.51233"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M81.486 58.4733C80.5403 60.6056 79.0156 63.9813 77.0221 68.1066C73.381 75.6409 72.365 77.0099 72.6401 79.6965C73.1171 84.3505 77.0866 89.2248 77.0866 89.2248"
                stroke="#262626"
                stroke-width="0.51233"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M89.2054 74.8138C89.2054 74.8138 102.157 74.8138 102.213 74.8138C112.498 74.8138 123.765 82.175 130.529 89.3666C135.941 95.1195 139.9 102.113 142.905 109.417C146.818 118.931 150.65 132.113 145.863 141.959C145.149 143.429 143.529 146.653 140.093 148.393C136.313 150.307 132.364 149.164 131.14 149.164C123.151 149.164 52.3519 149.186 52.3519 149.186C50.4599 130.795 54.1051 116.938 56.738 109.379C59.1839 102.355 62.0447 94.4253 69.5109 86.8951C76.6615 79.684 84.5452 76.3606 89.2054 74.8138Z"
                fill="#EE7B7F"
              />
              <path
                d="M95.685 80.3109C99.3287 80.3247 102.294 77.3824 102.307 73.7387L102.361 59.542L89.1671 59.4918L89.1133 73.689C89.0995 77.3322 92.0418 80.297 95.685 80.3109Z"
                fill="#F7A99C"
              />
              <path
                d="M89.1279 69.7153L97.7213 65.3005C97.6342 66.0444 97.1459 69.5298 94.0806 71.9368C92.1235 73.4737 90.067 73.8247 89.1172 73.9241L89.1279 69.7153Z"
                fill="#FF7769"
              />
              <path
                d="M102.68 55.1442C102.721 54.9295 103.287 54.6057 103.46 54.4945C103.754 54.307 104.071 54.1574 104.402 54.0503C105.046 53.8434 105.753 53.7952 106.415 53.9469C107.731 54.2486 108.897 55.3537 109.115 56.7073C109.242 57.4937 109.061 58.3068 108.724 59.0287C108.217 60.1138 107.358 61.0257 106.32 61.6236C105.46 62.119 104.481 62.3993 103.492 62.4582C103.294 62.47 101.266 62.4228 101.281 62.3455C101.748 59.9452 102.214 57.545 102.68 55.1442Z"
                fill="#F7A99C"
              />
              <path
                d="M104.49 55.2815C104.334 56.341 103.403 61.928 98.3674 65.8755C97.4396 66.6025 91.2722 71.4368 85.6299 69.0775C78.3789 66.0461 79.1699 54.1365 79.2211 53.4914C79.3938 51.311 79.9031 44.8889 85.2216 41.4624C89.562 38.6661 94.2657 39.6944 94.9328 39.8512C95.6357 40.0166 100.113 41.1463 102.853 45.5365C105.339 49.5204 104.745 53.5483 104.49 55.2815Z"
                fill="#F7A99C"
              />
              <path
                d="M83.3352 53.4992C83.2184 54.429 82.5016 55.1043 81.7336 55.008C80.9656 54.9117 80.4385 54.0796 80.5553 53.1498C80.6721 52.2199 81.3888 51.5441 82.1568 51.6409C82.9243 51.7378 83.452 52.5693 83.3352 53.4992Z"
                fill="#262626"
              />
              <path
                d="M91.7136 54.5518C91.5967 55.4817 90.88 56.1569 90.1125 56.0606C89.3451 55.9643 88.8174 55.1323 88.9342 54.2024C89.051 53.2725 89.7677 52.5973 90.5352 52.6936C91.3027 52.7899 91.8304 53.6219 91.7136 54.5518Z"
                fill="#262626"
              />
              <path
                d="M85.8662 54.8909L84.5818 56.6256C84.3456 56.9443 84.4343 57.3972 84.7734 57.6037L85.9528 58.3214"
                stroke="#262626"
                stroke-width="0.690518"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M94.5274 58.9313C94.3962 59.9744 93.4443 60.7142 92.4012 60.583C91.3581 60.4519 90.6183 59.5 90.7494 58.4569C90.8806 57.4138 91.8325 56.6739 92.8756 56.8051C93.9192 56.9363 94.6585 57.8882 94.5274 58.9313Z"
                fill="#FF7769"
              />
              <path
                d="M103.568 59.9147C103.561 59.7057 103.543 58.2891 104.68 57.267C105.571 56.4662 106.607 56.4139 106.889 56.4088"
                stroke="#262626"
                stroke-width="0.690518"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M104.57 57.5464C104.633 57.5366 105.396 57.4306 105.927 58.007C106.365 58.4803 106.325 59.0665 106.317 59.1638"
                stroke="#262626"
                stroke-width="0.690518"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M80.2084 55.9197C79.8477 55.8747 79.5003 55.9392 79.1914 56.0786C79.2283 57.1007 79.3267 58.3405 79.5511 59.6557C79.612 59.6695 79.6704 59.6895 79.734 59.6977C80.7771 59.8288 81.7295 59.089 81.8601 58.0459C81.9913 57.0028 81.252 56.0509 80.2084 55.9197Z"
                fill="#FF7769"
              />
              <path
                d="M84.1211 61.7993C84.3409 61.5324 85.0243 60.7731 86.2063 60.4775C87.5455 60.1429 88.6204 60.6296 88.9083 60.771"
                stroke="#262626"
                stroke-width="0.690518"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M78.6289 50.2106H86.9307C86.9307 50.2106 87.2437 47.5086 88.0665 46.1771C88.8893 44.8455 90.142 44.0232 90.142 44.0232C89.9535 44.5002 89.7388 45.1319 89.5743 45.8932C89.457 46.4363 89.3007 47.1566 89.31 48.0957C89.31 48.0957 89.3136 48.4513 89.5154 50.2101H96.7208C96.5553 49.7716 96.2003 48.6547 96.5056 47.2734C96.7721 46.0679 97.4048 45.2738 97.7219 44.9239C97.478 47.0859 97.7516 48.8084 98.0129 49.8971C98.4684 51.7953 99.2277 53.1914 99.7948 54.234C100.339 55.2356 100.874 56.0353 101.284 56.6061C101.284 56.6061 102.262 55.3708 103.055 54.8908C103.848 54.4107 104.658 53.9783 104.658 53.9783C104.84 53.0341 105.003 51.5996 104.749 49.8976C104.225 46.3943 102.282 44.1052 101.469 43.2506C98.7133 40.3519 95.4159 39.7007 94.215 39.4814C91.6528 39.0137 89.5984 39.4041 88.8893 39.5598C88.335 39.6817 87.0485 40.0019 85.551 40.7838C84.512 41.3258 81.5687 42.9048 79.8242 46.3641C79.4107 47.1823 78.8707 48.4851 78.6289 50.2106Z"
                fill="#262626"
              />
              <path
                d="M87.449 75.4323C87.449 75.4323 79.4115 108.362 78.4719 119.405C77.5323 130.447 77.1798 149.504 77.1798 149.504L52.3528 149.187C51.8472 145.621 51.3948 140.771 51.5695 135.029C51.9788 121.607 55.5636 111.625 56.9735 108.009C60.2924 99.4969 64.2999 89.2175 74.7125 81.6945C79.5273 78.2162 84.2111 76.4108 87.449 75.4323Z"
                fill="#CF3339"
              />
              <path
                d="M72.8702 86.7455C72.3026 89.9378 71.4152 95.2737 70.6216 101.941C69.6497 110.107 69.7957 112.066 68.1973 132.937C67.7182 139.194 66.918 149.294 66.918 149.294"
                stroke="#262626"
                stroke-width="0.51233"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M95.4186 137.438C96.3572 138.411 96.8982 139.346 97.2102 139.993C98.1903 142.027 98.2241 143.961 98.2482 145.328C98.2625 146.143 98.2159 146.819 98.1698 147.287C98.1698 147.287 86.0307 143.371 84.2293 143.527C82.6421 143.665 74.7328 145.688 72.8735 146.167C72.6153 146.234 72.3463 146.226 72.0912 146.148L69.4271 145.328L66.9151 145.435C66.0134 145.474 65.3955 144.552 65.7629 143.727C65.8541 143.522 65.9765 143.3 66.1379 143.057C67.2343 141.413 68.3307 140.238 68.3307 140.238L64.3053 140.964C63.9149 140.965 63.2755 140.911 63.0562 140.52C62.7294 139.936 63.5327 138.95 64.0819 138.29C65.3561 136.759 66.7486 135.935 67.1948 135.676C68.7677 134.764 70.2345 133.914 72.6378 133.19C74.5319 132.619 79.7474 130.963 86.1674 132.592C90.3747 133.66 93.464 135.77 95.4186 137.438Z"
                fill="#F7A99C"
              />
              <path
                d="M94.7779 136.909C94.8312 137.32 95.1129 139.821 93.3152 141.961C91.3114 144.345 88.4004 144.286 88.0469 144.273L98.171 147.286C98.171 147.286 98.2458 143.665 98.067 142.775C97.8882 141.884 97.2647 139.828 96.6223 138.948C95.9798 138.068 94.7779 136.909 94.7779 136.909Z"
                fill="#FF7769"
              />
              <path
                d="M69.4258 145.329C70.5908 144.054 72.6002 142.169 75.5804 140.681C78.389 139.279 80.9486 138.784 82.6224 138.593"
                stroke="#262626"
                stroke-width="0.51233"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M66.9141 145.435C68.7508 142.303 70.7837 140.674 72.2843 139.768C72.2843 139.768 73.393 139.098 79.2156 136.909"
                stroke="#262626"
                stroke-width="0.51233"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M68.1484 140.271C69.6255 138.091 71.1676 137.078 72.2865 136.557C74.2764 135.629 77.2987 135.147 77.2987 135.147"
                stroke="#262626"
                stroke-width="0.51233"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M103.566 74.8138C103.566 74.8138 101.027 93.6763 100.675 104.954C100.323 116.232 99.9618 135.498 99.9618 135.498L95.418 137.437C95.418 137.437 97.5867 143.84 97.7209 145.837C97.8556 147.834 97.7209 149.504 97.7209 149.504C104.366 149.374 109.767 149.539 113.48 149.713C116.638 149.863 121.341 149.891 130.749 149.949C134.094 149.969 136.92 149.954 140.094 148.394C140.469 148.209 143.656 146.601 146.021 143.37C147.551 141.278 149.953 136.845 148.605 125.865C148.038 121.246 146.07 106.793 134.86 93.0892C128.456 85.2598 122.136 81.4639 120.378 80.4531C115.046 77.3883 110.356 76.1398 109.382 75.8877C107.062 75.2888 105.055 74.9824 103.566 74.8138Z"
                fill="#CF3339"
              />
              <path
                d="M132.081 126.923C132.081 126.923 103.725 133.894 99.9618 135.499C96.1983 137.105 95.418 137.438 95.418 137.438"
                stroke="#262626"
                stroke-width="0.51233"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M134.272 97.5544C134.272 97.5544 148.212 123.791 144.062 134.521C143.427 136.161 142.025 139.71 138.518 142.288C135.229 144.705 131.686 145.088 125.579 145.25C120.238 145.392 113.135 145.361 104.668 144.702"
                stroke="white"
                stroke-width="0.51233"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M130.196 88.0063C124.445 90.2837 121.766 93.3535 120.411 95.4397C120.411 95.4397 116.256 101.84 121.375 129.602"
                stroke="#262626"
                stroke-width="0.51233"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M96.7106 149.952H58.6562V145.393H98.6769V147.986C98.6769 149.072 97.7967 149.952 96.7106 149.952Z"
                fill="#374ACC"
              />
              <path
                d="M62.8215 149.952H10.3098C9.10937 149.952 8.01759 148.979 7.87055 147.779L3.1023 108.777C2.95577 107.576 3.80983 106.603 5.0097 106.603H57.522C58.7224 106.603 59.8142 107.576 59.9612 108.777L64.729 147.779C64.876 148.979 64.0219 149.952 62.8215 149.952Z"
                fill="#374ACC"
              />
              <path
                d="M59.7473 149.952H7.23554C6.03515 149.952 4.94337 148.979 4.79633 147.779L0.0280786 108.777C-0.118448 107.576 0.735606 106.603 1.93548 106.603H54.4478C55.6482 106.603 56.7399 107.576 56.887 108.777L61.6547 147.779C61.8018 148.979 60.9477 149.952 59.7473 149.952Z"
                fill="#4450E4"
              />
              <path
                d="M0.0279027 108.776C0.408052 108.672 1.79954 120.832 9.74629 131.778C11.1209 133.671 13.6006 137.036 17.8129 140.315C17.8129 140.315 22.0965 143.649 38.0582 149.951C37.198 149.951 36.3372 149.951 35.477 149.951C33.3606 149.951 31.2442 149.951 29.1272 149.951C26.4483 149.951 23.7693 149.951 21.0903 149.951C18.543 149.951 15.9957 149.951 13.4479 149.951C12.081 149.951 10.7146 149.953 9.3477 149.952C8.259 149.952 6.9044 150.195 5.93251 149.587C5.12815 149.084 4.77669 148.089 4.66142 147.147C4.57996 146.481 4.58918 145.807 4.51438 145.14C4.44009 144.48 4.31252 143.825 4.23208 143.164C4.05584 141.722 3.87909 140.278 3.70284 138.836C3.4144 136.475 3.12596 134.116 2.83701 131.755C2.52603 129.21 2.21453 126.665 1.90354 124.12C1.61459 121.757 1.32564 119.393 1.03668 117.03C0.814843 115.216 0.625791 113.397 0.371163 111.587C0.0796471 109.51 -0.0648291 108.801 0.0279027 108.776Z"
                fill="#5D6FF4"
              />
              <path
                d="M1.9375 106.603H54.4498C55.6502 106.603 56.742 107.576 56.889 108.777L61.6567 147.779"
                stroke="#262626"
                stroke-width="0.51233"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M144.453 87.3729H150.284C152.844 87.3729 154.863 89.5493 154.671 92.1017L152.891 115.802"
                stroke="white"
                stroke-width="0.51233"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M138.414 29.3201C139.31 26.9724 140.21 25.1501 141.115 23.8543C142.019 22.5586 143.217 21.5105 144.706 20.712C145.577 20.223 146.488 19.8649 147.442 19.6358C148.394 19.4073 149.3 19.1258 150.157 18.7913C151.013 18.4575 151.797 18.0152 152.509 17.4662C153.22 16.9166 153.796 16.0658 154.236 14.9111C154.98 12.9617 154.963 11.3934 154.184 10.2075C153.405 9.02153 152.279 8.1477 150.808 7.586C148.698 6.7811 147.021 6.81493 145.779 7.68493C144.534 8.55557 143.434 9.9477 142.476 11.8613L134.776 8.92387C135.52 6.9745 136.513 5.3245 137.758 3.97513C139.001 2.62641 140.423 1.61854 142.022 0.952155C143.621 0.285771 145.36 -0.0308294 147.24 0.00236214C149.118 0.0349154 151.053 0.431304 153.043 1.19024C157.341 2.82939 160.399 4.9996 162.219 7.69897C164.037 10.399 164.196 13.7181 162.694 17.6583C162.207 18.9324 161.653 19.9964 161.031 20.853C160.407 21.709 159.689 22.4264 158.877 23.0054C158.063 23.5837 157.149 24.0675 156.135 24.4556C155.12 24.8443 153.999 25.2139 152.771 25.5656C151.637 25.9084 150.692 26.2658 149.935 26.6373C149.177 27.0094 148.537 27.4613 148.017 27.9911C147.495 28.5215 147.061 29.1203 146.716 29.786C146.37 30.4524 146.031 31.2235 145.698 32.0986L138.414 29.3201ZM133.781 40.7501L136.764 32.9303L144.823 36.0043L141.84 43.8241L133.781 40.7501Z"
                fill="#E6E7E7"
              />
            </svg>
            <div className="resume-card-content">
              <h6 className="large-body bold text-primary-burgundy-tint-800">
                Resume Journey
              </h6>
              <p className="Secondary-Grey-tint-500 small-body regular">
                Enter your application details to continue from where you left
                off.
              </p>
            </div>
          </div>
          <div className="resume-card-actions">
            <div className="resume-system-upgrade-box">
              <span className="resume-system-upgrade-icon" aria-hidden="true">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="10"
                    cy="10"
                    r="9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M10 9V14"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <circle cx="10" cy="6.25" r="1" fill="currentColor" />
                </svg>
              </span>
              <div className="resume-system-upgrade-content">
                <p className="resume-system-upgrade-text small-body regular">
                  <strong>SYSTEM UPGRADE:</strong> If your application number
                  starts with &apos;M&apos; (from our previous system), resume
                  your journey using the button below. All &apos;N&apos; series
                  applications should use the form on the right.
                </p>
                <button
                  type="button"
                  className="btn secondary-button regular resume-older-journey-btn"
                  onClick={handleResumeLegacyJourney}
                >
                  Resume Older Journey
                </button>
              </div>
            </div>
            <button
              type="button"
              className="btn primary-button regular resume-start-new-btn"
              onClick={handleStartNewJourney}
            >
              Start New Journey
            </button>
          </div>
        </div>

        <div className="resume-card-right">
          <div className="resume-card-right-content">
            <form
              onSubmit={onSubmit}
              className="resume-card-right-content-form"
            >
              <div className="form-row">
                <label
                  htmlFor="resume-application-number"
                  className="label-one"
                >
                  Application Number
                </label>
                <input
                  id="resume-application-number"
                  type="text"
                  className="form-input"
                  value={applicationNumber}
                  onChange={(event) => {
                    setApplicationNumber(event.target.value);
                    if (applicationError) setApplicationError("");
                    resetOtpState();
                  }}
                  placeholder="Enter application number (e.g. N0000061)"
                  autoComplete="off"
                  inputMode="text"
                  pattern="N[0-9]{7}"
                  maxLength={8}
                />
                {applicationError ? (
                  <p className="error-text">{applicationError}</p>
                ) : null}
              </div>

              <div className="form-row">
                <label htmlFor="resume-dob" className="label-one">
                  DOB
                </label>
                <div className="resume-dob-datepicker">
                  <DatePicker
                    id="resume-dob"
                    selected={ymdStringToLocalDate(dob)}
                    onChange={(date) => {
                      setDob(date ? localDateToYmd(date) : "");
                      if (dobError) setDobError("");
                      resetOtpState();
                    }}
                    onChangeRaw={(e) =>
                      handleDobRawInputChange(e, (iso) => {
                        setDob(iso);
                        if (dobError) setDobError("");
                        resetOtpState();
                      })
                    }
                    dateFormat="dd/MM/yyyy"
                    placeholderText="DD/MM/YYYY"
                    minDate={minResumeDobDate}
                    maxDate={new Date()}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    scrollableYearDropdown
                    yearDropdownItemNumber={120}
                    showIcon
                    toggleCalendarOnIconClick
                    openOnFocus
                    className="form-input"
                    wrapperClassName="resume-dob-datepicker-inner"
                    autoComplete="off"
                  />
                </div>
                {dobError ? <p className="error-text">{dobError}</p> : null}
              </div>

              {otpSent ? (
                <div className="form-row">
                  <label htmlFor="resume-otp" className="label-one">
                    OTP
                  </label>
                  <p className="small-text">
                    Enter the 6-digit OTP sent to {maskedOtpDestination}.
                  </p>
                  <input
                    id="resume-otp"
                    type="text"
                    className="form-input"
                    value={otp}
                    onChange={(event) => {
                      setOtp(event.target.value.replace(/\D/g, "").slice(0, 6));
                      if (otpError) setOtpError("");
                    }}
                    placeholder="Enter 6-digit OTP"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={6}
                  />
                  {otpError ? <p className="error-text">{otpError}</p> : null}
                  <div className="form-actions">
                    <button
                      type="button"
                      className="resume-button btn secondary-button regular"
                      onClick={onResendOtp}
                      disabled={isResendOtpDisabled}
                    >
                      {resendOtpLabel}
                    </button>
                  </div>
                </div>
              ) : null}

              {errorMessage ? (
                <p className="error-text">{errorMessage}</p>
              ) : null}

              <div className="form-actions">
                <button
                  type="submit"
                  className="resume-button btn primary-button regular"
                  disabled={otpSent ? isVerifyOtpDisabled : isSendOtpDisabled}
                >
                  {buttonLabel}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResumeJourney;
